import JSZip from 'jszip';
import { supabase } from '@/lib/supabase';
import { convertPhpZipToTheme, type SourceLanguage } from '@/lib/phpThemeConverter';
import { fetchXaiKey, ZIFEK_PAGE_DEFS } from '@/lib/themeReference';
import { md5 } from '@/lib/md5';
import type { ThemeCategory } from '@/lib/themeCategories';
import { getBusinessCategory } from '@/lib/themeBusinessCategories';
import type { ThemeReferenceFileRecord } from '@/lib/themeReferenceFiles';
import { getReferencePages } from '@/lib/themeReferenceFiles';
import { logAiUsage, readUsage } from '@/lib/aiUsageLog';
import { buildThemePreviewDocument } from '@/lib/themePreviewRender';
import { CONVERSION_PROMPT_KEY, DEFAULT_CONVERSION_PROMPT, fetchPromptContent } from '@/lib/aiPromptStore';

// ============================================================
// Conversion de thème pilotée par l'IA, basée sur la catégorie
// et son « fichier de référence » (instructions pour l'IA).
//
// Entrée acceptée : index.html, index.php, un ZIP, ou plusieurs
// fichiers à la fois (HTML / PHP / CSS).
// ============================================================

export interface AiConvertInput {
  files: File[];
  category: ThemeCategory;
  themeName: string;
  userId: number;
  userIdcommerce: string;
  onProgress: (msg: string) => void;
  /** Fichier de référence à appliquer (prioritaire sur les instructions de la catégorie). */
  referenceFile?: ThemeReferenceFileRecord | null;
  /** Catégorie d'activité (produits / services / mixte). */
  businessCategoryId?: string;
  /** Nom du type de thème (catégorie typetheme). */
  themeTypeName?: string;
}

export interface ThemePreviewSnapshot {
  documentHtml: string;
  stylesheet: string;
}

export interface AiConvertResult {
  themeId: number;
  pageCount: number;
  stylesheet: string;
  warnings: string[];
  usedAi: boolean;
  /** Aperçu visuel avant la passe IA (conversion automatique). */
  previewBefore: ThemePreviewSnapshot;
  /** Aperçu visuel après la passe IA (résultat final). */
  previewAfter: ThemePreviewSnapshot;
}

const XAI_ENDPOINT = 'https://api.x.ai/v1/chat/completions';
const XAI_MODEL = 'grok-4.6';

// ── Utilitaires ─────────────────────────────────────────────

function hasPhp(files: File[]): boolean {
  return files.some((f) => /\.(php|phtml|php3|php4|php5)$/i.test(f.name));
}

/** Construit un unique fichier ZIP à partir de l'entrée utilisateur. */
async function buildZip(files: File[]): Promise<File> {
  if (files.length === 1 && files[0].name.toLowerCase().endsWith('.zip')) {
    return files[0];
  }
  const zip = new JSZip();
  for (const file of files) {
    if (file.name.toLowerCase().endsWith('.zip')) {
      // Un ZIP parmi d'autres fichiers → on fusionne son contenu.
      try {
        const inner = await JSZip.loadAsync(file);
        const entries = Object.values(inner.files).filter((e) => !e.dir);
        for (const entry of entries) {
          const content = await entry.async('string');
          zip.file(entry.name, content);
        }
      } catch {
        // ZIP illisible → ignoré
      }
    } else {
      zip.file(file.name, await file.text());
    }
  }
  const blob = await zip.generateAsync({ type: 'blob' });
  return new File([blob], 'theme-source.zip', { type: 'application/zip' });
}

/**
 * Extrait les blocs <style> les plus volumineux répétés dans les pages
 * pour les fusionner dans la feuille de style (évite les timeouts).
 */
function extractInlineStyles(pages: Record<string, string>, baseCss: string): string {
  const unique = new Map<string, string>();
  for (const key of Object.keys(pages)) {
    const pageHtml = pages[key] || '';
    if (!pageHtml) continue;
    const cleaned = pageHtml.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_m, css: string) => {
      const normalized = css.trim();
      if (normalized) {
        const dedupeKey = md5(normalized.replace(/\s+/g, ' '));
        if (!unique.has(dedupeKey)) unique.set(dedupeKey, normalized);
      }
      return '';
    });
    pages[key] = cleaned.trim();
  }
  if (unique.size === 0) return baseCss;
  const extracted = Array.from(unique.values()).join('\n');
  return `${baseCss || ''}\n/* === CSS extrait des balises <style> des pages === */\n${extracted}`;
}

function truncate(value: string, max: number): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}\n<!-- ... tronqué ... -->` : value;
}

// ── Appel à l'IA (xAI Grok) ─────────────────────────────────

interface AiThemeJson {
  stylesheet?: string;
  pages?: Record<string, string>;
}

function buildSystemPrompt(
  category: ThemeCategory,
  reference: string,
  pageDefs: { key: string; label: string }[],
  opts?: { businessCategoryId?: string; themeTypeName?: string; template?: string },
): string {
  const refBlock = reference.trim()
    ? `📌 INSTRUCTIONS DE RÉFÉRENCE (À RESPECTER ABSOLUMENT) :\n${reference.trim()}`
    : `📌 Aucune instruction de référence fournie. Conserve le design existant à l'identique.`;
  const businessBlock = opts?.businessCategoryId ? `\n\n${getBusinessCategory(opts.businessCategoryId).promptBlock}` : '';
  const typeBlock = opts?.themeTypeName ? `\n\n🗂️ TYPE DE THÈME : ${opts.themeTypeName}` : '';
  const pagesList = pageDefs.map((p) => `- ${p.key} : ${p.label}`).join('\n');

  // Prompt personnalisé (modifiable et sauvegardé depuis le Theme Builder) OU prompt par défaut.
  const template = (opts?.template && opts.template.trim()) ? opts.template : DEFAULT_CONVERSION_PROMPT;

  return template
    .replace(/\{\{CATEGORIE\}\}/g, category.titre)
    .replace(/\{\{TYPE_BLOCK\}\}/g, typeBlock)
    .replace(/\{\{BUSINESS_BLOCK\}\}/g, businessBlock)
    .replace(/\{\{REFERENCE_BLOCK\}\}/g, refBlock)
    .replace(/\{\{PAGES_LIST\}\}/g, pagesList)
    .replace(/\{\{PAGES_KEYS\}\}/g, pageDefs.map((p) => p.key).join(', '));
}

function buildUserContent(
  converted: { pages: Record<string, string>; stylesheet: string },
  pageDefs: { key: string; label: string }[],
): string {
  const pageList = pageDefs.map((def) => {
    const content = converted.pages[def.key] || '';
    return `### ${def.key} (${def.label})\n${content.trim() ? truncate(content, 3500) : '<!-- vide : conserve une page cohérente avec le design d\'accueil -->'}`;
  }).join('\n\n');

  return `Voici la conversion automatique du thème à rendre dynamique — SANS MODIFIER LE DESIGN.

===== CSS DE BASE (à conserver tel quel) =====
${truncate(converted.stylesheet || '', 9000)}

===== PAGES CONVERTIES =====
${pageList}

Conserve le design à l'identique et rends dynamiques uniquement les zones de contenu. Renvoie le JSON { "stylesheet": "...", "pages": {...} }.`;
}

async function callAi(system: string, user: string, apiKey: string): Promise<{ json: AiThemeJson; usage: { requestTokens: number; responseTokens: number } }> {
  const response = await fetch(XAI_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: XAI_MODEL,
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: user },
      ],
      temperature: 0.6,
      max_tokens: 32000,
      response_format: { type: 'json_object' },
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Erreur IA ${response.status}: ${errText.slice(0, 180)}`);
  }

  const result = await response.json();
  const content: string = result?.choices?.[0]?.message?.content || '';
  if (!content) throw new Error('Réponse vide de l\'IA');

  let parsed: AiThemeJson;
  try {
    parsed = JSON.parse(content);
  } catch {
    const match = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (!match) throw new Error('Impossible de lire la réponse JSON de l\'IA');
    parsed = JSON.parse(match[1].trim());
  }
  return { json: parsed, usage: readUsage(result?.usage) };
}

// ── Sauvegarde en thème Zifek ───────────────────────────────

async function insertRowWithRetry(table: string, row: Record<string, unknown>, maxAttempts = 5): Promise<void> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const { error } = await supabase.from(table).insert(row);
      if (!error) return;
      if (/duplicate|already exists|23505/i.test(error.message)) return;
      lastError = new Error(error.message);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('erreur inconnue');
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
    }
  }
  throw lastError || new Error(`Échec d'insertion dans ${table}`);
}

// ── Point d'entrée ──────────────────────────────────────────

export async function convertFilesToZifekTheme(input: AiConvertInput): Promise<AiConvertResult> {
  const {
    files, category, themeName, userId, userIdcommerce, onProgress,
    referenceFile, businessCategoryId, themeTypeName,
  } = input;

  if (files.length === 0) {
    throw new Error('Sélectionne au moins un fichier (index.html, index.php ou un ZIP).');
  }

  onProgress('Préparation des fichiers...');
  const zipFile = await buildZip(files);

  const language: SourceLanguage = hasPhp(files) ? 'php' : 'html';
  onProgress('Conversion automatique de la structure...');
  const converted = await convertPhpZipToTheme(zipFile, onProgress, undefined, language);

  const warnings = [...converted.warnings];

  // Pages attendues : celles du fichier de référence s'il est fourni,
  // sinon la structure complète par défaut.
  const refPages = referenceFile?.contenu ? getReferencePages(referenceFile.contenu) : null;
  const pageDefs: { key: string; label: string }[] = refPages && refPages.length > 0
    ? refPages.map((p) => ({ key: p.key, label: p.label }))
    : ZIFEK_PAGE_DEFS;

  // ── Passe IA pilotée par la catégorie + fichier de référence ──
  let finalPages: Record<string, string> = { ...converted.pages };
  let finalStylesheet = extractInlineStyles(finalPages, converted.stylesheet);
  let usedAi = false;

  // Prompt de conversion personnalisé (éditable depuis le Theme Builder).
  const customPrompt = await fetchPromptContent(CONVERSION_PROMPT_KEY);

  // Le fichier de référence sélectionné est prioritaire sur les instructions de la catégorie.
  const reference = (referenceFile?.contenu || '').trim() || category.reference_instructions || '';
  try {
    const apiKey = await fetchXaiKey();
    if (apiKey) {
      onProgress('Conversion IA selon le fichier de référence, la catégorie et le type de thème...');
      const startedAt = Date.now();
      const aiResponse = await callAi(
        buildSystemPrompt(category, reference, pageDefs, { businessCategoryId, themeTypeName, template: customPrompt || undefined }),
        buildUserContent({ pages: converted.pages, stylesheet: converted.stylesheet }, pageDefs),
        apiKey,
      );
      const aiResult = aiResponse.json;
      await logAiUsage({
        provider: 'xai',
        model: XAI_MODEL,
        endpoint: XAI_ENDPOINT,
        requestTokens: aiResponse.usage.requestTokens,
        responseTokens: aiResponse.usage.responseTokens,
        durationMs: Date.now() - startedAt,
        status: 'success',
        userId,
      });

      if (aiResult?.stylesheet && aiResult.stylesheet.trim().length > 200) {
        finalStylesheet = aiResult.stylesheet;
        usedAi = true;
      } else {
        warnings.push('L\'IA n\'a pas renvoyé de CSS exploitable — CSS de la conversion automatique conservé.');
      }

      if (aiResult?.pages) {
        for (const def of pageDefs) {
          const aiContent = aiResult.pages[def.key];
          if (typeof aiContent === 'string' && aiContent.trim().length > 0) {
            finalPages[def.key] = aiContent.trim();
          }
        }
        usedAi = true;
      }
      // Les pages sans contenu IA gardent la conversion automatique.
      finalStylesheet = extractInlineStyles(finalPages, finalStylesheet);
    } else {
      warnings.push('Aucune clé IA configurée — conversion automatique utilisée sans passe IA.');
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'erreur inconnue';
    warnings.push(`Passe IA ignorée (${msg}). Conversion automatique conservée.`);
  }

  onProgress('Enregistrement du thème...');

  const cleanName = themeName.trim() || `Thème ${category.titre}`;
  const dossierSlug = cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'theme';
  const authorIdInt = Number.isNaN(userId) || userId < 1 ? 1 : userId;

  const insertPayload: Record<string, unknown> = {
    titre: cleanName,
    description: `Thème converti par IA — catégorie ${category.titre}`,
    typetheme: String(category.id),
    version: '1.0',
    prix: '0',
    stylesheet: finalStylesheet,
    active: 0,
    idauteur: authorIdInt,
    dossier: dossierSlug,
    imagecouverture: '',
    is_imported: true,
  };
  if (userIdcommerce) insertPayload.idcommerce = userIdcommerce;

  const { data: newTheme, error: themeError } = await supabase
    .from('sitewebtheme')
    .insert(insertPayload)
    .select('id')
    .single();

  if (themeError || !newTheme) {
    throw new Error(`Erreur création du thème : ${themeError?.message || 'inconnue'}`);
  }

  const newThemeId = newTheme.id as number;

  const pageEntries = pageDefs.map((def) => ({
    idtheme: newThemeId,
    page_key: def.key,
    title: def.label,
    content: finalPages[def.key] || '',
  }));

  for (let i = 0; i < pageEntries.length; i += 4) {
    onProgress(`Enregistrement des pages (${Math.min(i + 4, pageEntries.length)}/${pageEntries.length})...`);
    const batch = pageEntries.slice(i, i + 4);
    for (const row of batch) {
      await insertRowWithRetry('sitewebthemepage', row, 5);
    }
  }

  try {
    await insertRowWithRetry('sitewebthemecontenu', {
      idtheme: newThemeId,
      idshop: 1,
      titrenavmenudefaut: `${cleanName} | Services | À propos | Contact`,
      descriptionnavmenudefault: `Navigation du thème ${cleanName}`,
      imagebannierenavmenudefault: '',
    }, 3);
  } catch { /* non bloquant */ }

  try {
    await insertRowWithRetry('sitewebthemeparamettre', {
      idtheme: newThemeId,
      idcommerce: 1,
      titre: `${cleanName} - Paramètres`,
      descriptionbanniere: `Bannière principale du thème ${cleanName}`,
      imageaboutus: '',
      titreblocdecouvert: 'Découvrez nos services',
      descriptionblocdecouvert: `Contenu découverte du thème ${cleanName}`,
      fichierblocdecouvert: '',
    }, 3);
  } catch { /* non bloquant */ }

  const pageCount = pageEntries.filter((p) => p.content.trim()).length;

  const previewBefore: ThemePreviewSnapshot = {
    stylesheet: converted.stylesheet,
    documentHtml: buildThemePreviewDocument({
      stylesheet: converted.stylesheet,
      pages: converted.pages,
      title: 'Avant — conversion automatique',
    }),
  };
  const previewAfter: ThemePreviewSnapshot = {
    stylesheet: finalStylesheet,
    documentHtml: buildThemePreviewDocument({
      stylesheet: finalStylesheet,
      pages: finalPages,
      title: 'Après — conversion IA',
    }),
  };

  return {
    themeId: newThemeId,
    pageCount,
    stylesheet: finalStylesheet,
    warnings,
    usedAi,
    previewBefore,
    previewAfter,
  };
}